import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import SolidExclamationCircleIcon from 'LuxKit/Icons/solid/SolidExclamationCircleIcon'

import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_HOTEL } from 'constants/offer'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import getLPCRegionLOS from 'lib/offer/getLPCRegionLOS'
import getOfferStaysFrom from 'lib/offer/getOfferStaysFrom'
import Group from 'components/utils/Group'
import TextButton from 'LuxKit/Button/TextButton'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import FrontPageTileFlightPricing from './FrontPageTileFlightPricing'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import { useOfferPackagePrice } from 'hooks/Offers/useOfferPrice'
import { unique } from 'lib/array/arrayUtils'
import useOfferTilePriceBlockExperiment from 'hooks/Experiments/useOfferTilePriceBlockExperiment'
import OfferTilePriceBlockSavingsLabel from 'components/OfferList/OfferListTiles/Support/OfferTilePriceBlockSavingsLabel'
import OfferTilePriceBlockCaption from 'components/OfferList/OfferListTiles/Support/OfferTilePriceBlockCaption'

export const StyledExclamationIcon = styled(SolidExclamationCircleIcon)`
  position: relative;
  margin-inline-start: ${rem(5)};
  top: ${rem(3)};
`

interface Props {
  offer: App.Offer | App.OfferSummary
  pkg: App.Package
}

function HeroOfferTilePricing(props: Props) {
  const {
    offer,
    pkg,
  } = props

  const intl = useIntl()

  const {
    totalPrice,
    totalMemberPrice,
    totalValue,
    memberPriceOrPrice,
    valueOffPercent,
    saleUnit,
  } = useOfferPackagePrice(pkg, offer)
  const priceBlockVariation = useOfferTilePriceBlockExperiment(offer, undefined, totalPrice > 0)

  const shouldShowRate = valueOffPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE

  const shouldShowValue = pkg.value > 0 && memberPriceOrPrice && totalValue > memberPriceOrPrice && pkg.shouldDisplayValue &&
    (offer.type == OFFER_TYPE_HOTEL || !pkg.roomRate?.inclusionsHideValue) &&
    !offer.isDiscountPillHidden

  const staysFrom = getOfferStaysFrom(offer)
  const LPCRegionLOS = getLPCRegionLOS(offer)
  const showStaysFromLabel = staysFrom && offer.type === OFFER_TYPE_ALWAYS_ON && LPCRegionLOS && staysFrom < pkg.duration

  const durationText = useMemo(() => {
    if (offer.type === 'rental') {
      return intl.formatMessage(
        {
          id: 'h8WwWFDRLVdDVu_GYooP',
          defaultMessage: '{durationLabel} from',
          description: 'Rental offer duration label followed by "from" for pricing',
        },
        { durationLabel: pkg.durationLabel.toLocaleLowerCase() },
      )
    }
    if (offer.tileDurationLabel) {
      return intl.formatMessage(
        {
          id: 'sNwZABpb2U--yjLAqFIU',
          defaultMessage: '{durationLabel} from',
          description: 'Offer tile duration label followed by "from" for pricing',
        },
        { durationLabel: offer.tileDurationLabel },
      )
    }
    if (LPCRegionLOS) {
      return intl.formatMessage(
        {
          id: 'bZNEYTMzQor2DnEhK32Y',
          defaultMessage: '{count, plural, one {# night} other {# nights}} from',
          description: 'Night count followed by "from" for hero hotel offer pricing',
        },
        { count: pkg.duration },
      )
    }
    return intl.formatMessage({
      id: 'Fg3aOe2S-hkD54PiOz9L',
      defaultMessage: 'From',
      description: 'Price label prefix when no duration is shown in the price breakdown',
    })
  }, [intl, LPCRegionLOS, offer.tileDurationLabel, offer.type, pkg.duration, pkg.durationLabel])

  const offerLocations = useMemo(() => unique(offer.locations.concat(offer.location)),
    [offer.locations, offer.location])
  const vendorName = offer.vendorName

  return (
    <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="end" gap={4}>
      <VerticalSpacer gap={8}>
        <Group direction="vertical">
          {priceBlockVariation && <OfferTilePriceBlockSavingsLabel
            offer={offer}
            price={totalPrice}
            memberPrice={totalMemberPrice}
            discountPercentage={shouldShowValue && shouldShowRate ? valueOffPercent : undefined}
            variation={priceBlockVariation}
            offerConfig={offer.luxPlus}
          />}
          <OfferTilePriceBlockCaption
            priceBlockExperimentVariation={priceBlockVariation}
            duration={pkg.duration}
            durationLabel={offer.tileDurationLabel}
            saleUnit={saleUnit}
          >{durationText}
          </OfferTilePriceBlockCaption>
          <LuxPlusPriceStack
            price={totalPrice}
            memberPrice={totalMemberPrice}
            size="L"
            saleUnit={saleUnit}
            testid="hero-price"
            offerConfig={offer.luxPlus}
            presentation={priceBlockVariation ? 'price-block-experiment' : undefined}
          />
          {!priceBlockVariation && <PriceRowTaxesAndFees />}
          {shouldShowValue && <PriceRowValueDiscountWithCaption
            weight={priceBlockVariation ? 'normal' : undefined}
            colour={priceBlockVariation ? 'light-foreground-alt' : undefined}
            size="L"
            originalValue={Math.ceil(totalValue)}
            discountPercentage={priceBlockVariation ? undefined : (shouldShowRate ? valueOffPercent : undefined)}
          />}
          {priceBlockVariation && <PriceRowTaxesAndFees />}
          {staysFrom && showStaysFromLabel && <PriceRowCaption>
            {intl.formatMessage(
              {
                id: 'lxT4uz2txNsXlVtkWr0a',
                defaultMessage: 'Stays from {count, plural, one {# night} other {# nights}} available',
                description: 'Caption showing minimum stay duration available for hotel offer tile',
              },
              { count: staysFrom },
            )}
          </PriceRowCaption>}
          {!!pkg.roomOccupancy && offer.type === 'tour' && <PriceRowCaption>
            {pkg.roomOccupancy}
          </PriceRowCaption>}
        </Group>
        <PriceRowAgentHubCommission
          productType={offer.productType}
          offerId={offer.id}
          offerLocations={offerLocations}
          vendorName={vendorName}
          packageId={pkg.id}
        />
        <FrontPageTileFlightPricing offer={offer} />
      </VerticalSpacer>
      <CSSBreakpoint min="desktop">
        <TextButton kind="primary">
          {intl.formatMessage({
            id: 'ZeDMd6jBjq32p9aRIRRT',
            defaultMessage: 'View offer',
            description: 'Call-to-action button text on hero hotel offer tile',
          })}
        </TextButton>
      </CSSBreakpoint>
    </Group>
  )
}

export default HeroOfferTilePricing
